import type { Audience, BrandProfile, Pillar } from './brand';
import { CHANNEL_RULES, type Channel } from './channels';

/**
 * What the writer is asked to produce for one slot, as plain prompt text (the API's LLM gateway
 * sends it with a structured-output schema). The rules in the prompt mirror guardrails.ts, but the
 * guardrails check is what actually decides: a prompt is a request, not a guarantee.
 */

export type DraftBrief = {
  channel: Channel;
  pillar: Pillar;
  audience: Audience | null;
  language: 'en' | 'fr';
  /** Optional extra direction from the reviewer ("shorter", "mention the rate card"). */
  direction?: string | null;
  /** Recent posts on this channel, so the writer does not repeat itself. */
  recent?: readonly string[];
  /** Destination for the call to action (becomes a tracked short link). */
  ctaUrl?: string | null;
};

export type Draft = { title: string | null; body: string; hashtags: string[]; imageIdea: string | null; ctaUrl: string | null };

export function draftSystemPrompt(brand: Pick<BrandProfile, 'name' | 'description' | 'voice' | 'guardrails'>): string {
  const g = brand.guardrails;
  return [
    `You write marketing content for ${brand.name}. ${brand.description}`,
    `Voice: ${brand.voice || 'clear, warm, specific'}.`,
    'Rules you never break:',
    '- Never write reviews, ratings, testimonials or quotes attributed to customers or users.',
    '- Never invent numbers, percentages, user counts, rankings ("#1", "leading") or guarantees.' +
      (g.approvedClaims.length ? ` You may use only these approved claims: ${g.approvedClaims.map((c) => `"${c}"`).join('; ')}.` : ''),
    '- No engagement bait (no "comment YES", "tag a friend", "share if"). No fake urgency.',
    '- Speak to one specific pain; be concrete; one clear call to action at most.',
    '- No placeholders like [link] or {{name}}; if a link is needed, write the exact URL you are given.',
    g.bannedPhrases.length ? `- Never use these words or phrases: ${g.bannedPhrases.map((p) => `"${p}"`).join(', ')}.` : '',
    g.avoidTopics.length ? `- Stay away from: ${g.avoidTopics.join(', ')}.` : '',
  ]
    .filter(Boolean)
    .join('\n');
}

export function draftUserPrompt(brief: DraftBrief): string {
  const rules = CHANNEL_RULES[brief.channel];
  const lines = [
    `Write one ${rules.label} ${brief.channel === 'email' ? 'email' : brief.channel === 'blog' ? 'blog post (Markdown, with ## subheadings)' : 'post'}.`,
    `Content pillar: ${brief.pillar.name}. ${brief.pillar.description}`,
    brief.audience ? `Audience: ${brief.audience.name}. ${brief.audience.description}${brief.audience.pains.length ? ` Their pains: ${brief.audience.pains.join('; ')}.` : ''}` : '',
    `Length: ${rules.idealLength[0]}-${rules.idealLength[1]} characters (hard limit ${rules.maxLength}).`,
    rules.needsTitle ? (brief.channel === 'email' ? 'Include a subject line under 60 characters as the title.' : 'Include a title under 70 characters.') : 'No title.',
    rules.clickableLinks ? '' : 'Links are not clickable here: if there is a call to action, say "link in bio".',
    rules.maxHashtags ? `Up to ${Math.min(rules.maxHashtags, 5)} relevant hashtags, returned separately (not in the body).` : 'No hashtags.',
    rules.requiresMedia ? 'Also describe one image to go with it (a real product screenshot or a simple quote card; never a fake person).' : '',
    brief.ctaUrl && rules.clickableLinks ? `If you include a call to action, link to exactly: ${brief.ctaUrl}` : '',
    brief.language === 'fr' ? 'Write in Canadian French (Quebec).' : 'Write in Canadian English.',
    brief.recent?.length ? `Do not repeat these recent posts:\n${brief.recent.map((r) => `- ${r.slice(0, 160)}`).join('\n')}` : '',
    brief.direction ? `Reviewer's direction: ${brief.direction}` : '',
  ];
  return lines.filter(Boolean).join('\n');
}

/**
 * Deterministic draft used without an Anthropic key (local dev, tests, demos). Clearly marked so it
 * is never mistaken for finished copy.
 */
export function stubDraft(brand: Pick<BrandProfile, 'name'>, brief: DraftBrief): Draft {
  const rules = CHANNEL_RULES[brief.channel];
  const who = brief.audience ? brief.audience.name.toLowerCase() : 'you';
  const pain = brief.audience?.pains[0] ?? brief.pillar.description;
  const cta = brief.ctaUrl ? (rules.clickableLinks ? `\n\nSee how it works: ${brief.ctaUrl}` : '\n\nLink in bio.') : '';
  const body =
    brief.language === 'fr'
      ? `[Brouillon de démonstration] ${brief.pillar.name} : pour ${who}. ${pain}. ${brand.name} règle ça en un seul endroit.${cta}`
      : `[Demo draft] ${brief.pillar.name}, for ${who}: ${pain}. ${brand.name} keeps it in one place, so nothing slips.${cta}`;
  return {
    title: rules.needsTitle ? `${brief.pillar.name}: a better way` : null,
    body: body.slice(0, rules.maxLength),
    hashtags: rules.maxHashtags ? [`#${brand.name.replace(/[^\p{L}\p{N}]/gu, '')}`] : [],
    imageIdea: rules.requiresMedia ? `Quote card: "${brief.pillar.name}" on the brand background.` : null,
    ctaUrl: brief.ctaUrl ?? null,
  };
}

/** Body as published: hashtags appended on their own line where the channel uses them. */
export function composeBody(channel: Channel, draft: Pick<Draft, 'body' | 'hashtags'>): string {
  const max = CHANNEL_RULES[channel].maxHashtags;
  const tags = max ? draft.hashtags.slice(0, max).map((t) => (t.startsWith('#') ? t : `#${t}`)) : [];
  return tags.length ? `${draft.body.trim()}\n\n${tags.join(' ')}` : draft.body.trim();
}
